import { QUESTION_TYPE } from '../../../../constants/questionTypes';
import type { CfmMatrixQuestion } from '../../../../types/index';
import type { ApiQuestion } from '../../../../internalTypes';
import type { ResolvedQuestionShell } from '../shared/resolveQuestionShell';
import { QUESTION_CONFIG_KEY } from '../../../../constants/questionConfigKeys';
import { detectMatrixFormat } from '../shared/detectMatrixFormat';
import { buildCfmStatementRows } from './buildCfmStatementRows';
import { buildScaleColumns } from '../matrix/buildScaleColumns';
import { getConfigFlag, getConfigValue, hasNotApplicableOption } from '../shared/configUtils';

export function mapCfmMatrixQuestion(
  raw: ApiQuestion,
  shell: ResolvedQuestionShell,
  language?: string
): CfmMatrixQuestion {
  const configs = raw.questionConfigs ?? [];
  const matrixTypeValue = getConfigValue(configs, QUESTION_CONFIG_KEY.MATRIX_TYPE)?.toLowerCase();
  const statementLayout = matrixTypeValue === 'bipolar' ? 'bipolar' : 'likert';
  const isBipolar = statementLayout === 'bipolar';

  const statementRows = buildCfmStatementRows(raw, language, isBipolar);
  const scaleColumns = buildScaleColumns(raw, language);

  const gridLayout = detectMatrixFormat(configs);

  const hasMultiConfig = configs.some(
    c =>
      c.key.includes('MULTI') ||
      c.value?.[0]?.toUpperCase().includes('MULTI') ||
      (c.key.includes('ANSWER') && c.value?.[0]?.toUpperCase().includes('MULTI')) ||
      (c.key.includes('SELECT') && c.value?.[0]?.toUpperCase().includes('MULTI'))
  );
  const selectionMode = hasMultiConfig ? 'multiple' : 'single';

  const transposeTable = getConfigFlag(configs, QUESTION_CONFIG_KEY.TRANSPOSE_TABLE);
  const repeatColumnHeaders = getConfigFlag(configs, QUESTION_CONFIG_KEY.REPEAT_SCALE);

  const notApplicable = hasNotApplicableOption(configs);

  const effectiveTranspose = notApplicable ? false : transposeTable;

  if (notApplicable) {
    scaleColumns.push({ id: 'na', label: 'N/A' });
  }

  const showColumnHeaders =
    getConfigValue(configs, QUESTION_CONFIG_KEY.ENABLE_SCALE)?.toLowerCase() !== 'false';

  let scaleAnchorLabels: string[] | undefined;
  if (statementLayout === 'bipolar') {
    scaleAnchorLabels = (raw.answerDetails ?? [])
      .filter(a => a.additional?.isHintText && !a.hidden)
      .map(a => (language && a.translations?.[language]?.answerText) || a.answerText || '')
      .filter(Boolean);
    if (scaleAnchorLabels.length === 0) scaleAnchorLabels = undefined;
  }

  return {
    ...shell,
    type: QUESTION_TYPE.CFM_MATRIX,
    statementRows,
    scaleColumns,
    statementLayout,
    selectionMode,
    gridLayout,
    transposeTable: effectiveTranspose,
    repeatColumnHeaders: !!repeatColumnHeaders,
    hasNotApplicableOption: notApplicable,
    showColumnHeaders,
    ...(scaleAnchorLabels ? { scaleAnchorLabels } : {}),
  };
}
